import { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Plus, Pencil, Trash2, Search, Crown } from 'lucide-react';
import api from '@/lib/api';

export default function AdminOwners() {
    const [owners, setOwners] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [meta, setMeta] = useState({});
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({ name: '', email: '', password: '', phone: '', plan_id: '' });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [plans, setPlans] = useState([]);

    const fetchPlans = async () => {
        try {
            const res = await api.get('/admin/plans');
            setPlans(res.data.data);
        } catch {}
    };

    useEffect(() => { fetchPlans(); }, []);

    const fetchOwners = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get('/admin/owners', { params: { search, page, per_page: 10 } });
            setOwners(res.data.data);
            setMeta({ last_page: res.data.last_page, current_page: res.data.current_page, total: res.data.total });
        } catch {} finally { setLoading(false); }
    }, [search, page]);

    useEffect(() => { fetchOwners(); }, [fetchOwners]);

    const openCreate = () => {
        setEditing(null);
        setForm({ name: '', email: '', password: '', phone: '', plan_id: '' });
        setError('');
        setDialogOpen(true);
    };

    const openEdit = (owner) => {
        setEditing(owner);
        setForm({ name: owner.name || '', email: owner.email || '', password: '', phone: owner.phone || '', plan_id: owner.subscription?.plan_id ?? '' });
        setError('');
        setDialogOpen(true);
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError('');
        try {
            if (editing) {
                const data = { ...form };
                if (!data.password) delete data.password;
                await api.put(`/admin/owners/${editing.id}`, data);
                const currentPlanId = editing.subscription?.plan_id ?? '';
                if (form.plan_id && form.plan_id !== currentPlanId) {
                    await handleAssignPlan(editing.id, form.plan_id);
                }
            } else {
                await api.post('/admin/owners', form);
            }
            setDialogOpen(false);
            fetchOwners();
        } catch (err) {
            setError(err.response?.data?.message || 'Error al guardar.');
        } finally { setSaving(false); }
    };

    const handleDelete = async (id) => {
        if (!confirm('¿Estás seguro de eliminar este propietario?')) return;
        try {
            await api.delete(`/admin/owners/${id}`);
            fetchOwners();
        } catch {}
    };

    const handleAssignPlan = async (ownerId, planId) => {
        try {
            await api.post(`/admin/owners/${ownerId}/assign-plan`, { plan_id: planId });
            fetchOwners();
        } catch {}
    };

    const handleLifetimeFree = async (ownerId) => {
        if (!confirm('¿Asignar cuenta de por vida gratuita a este propietario?')) return;
        try {
            await api.post(`/admin/owners/${ownerId}/lifetime-free`);
            fetchOwners();
        } catch {}
    };

    return (
        <div>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
                <h1 className="text-2xl font-bold uppercase tracking-wide">Propietarios</h1>
                <Button onClick={openCreate} className="w-full sm:w-auto"><Plus className="h-4 w-4 mr-2" />Nuevo Propietario</Button>
            </div>

            <Card className="bg-[#0a0a0a] border-hairline-dark">
                <CardHeader>
                    <div className="flex items-center gap-2">
                        <Search className="h-4 w-4 text-white/60" />
                        <Input placeholder="Buscar propietarios..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} className="max-w-sm" />
                    </div>
                </CardHeader>
                <CardContent>
                    {loading ? (
                        <p className="text-white/60 text-center py-8">Cargando...</p>
                    ) : owners.length === 0 ? (
                        <p className="text-white/60 text-center py-8">No se encontraron propietarios.</p>
                    ) : (
                        <>
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm min-w-[550px]">
                                    <thead>
                                        <tr className="border-b border-hairline-dark">
                                            <th className="text-left py-3 px-2 font-medium">Nombre</th>
                                            <th className="text-left py-3 px-2 font-medium">Email</th>
                                            <th className="text-left py-3 px-2 font-medium">Plan</th>
                                            <th className="text-left py-3 px-2 font-medium">Estado</th>
                                            <th className="text-left py-3 px-2 font-medium">Gimnasios</th>
                                            <th className="text-right py-3 px-2 font-medium">Acciones</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {owners.map(owner => (
                                            <tr key={owner.id} className="border-b border-hairline-dark last:border-0 hover:bg-white/5">
                                                <td className="py-3 px-2 font-medium">{owner.name}</td>
                                                <td className="py-3 px-2">{owner.email}</td>
                                                <td className="py-3 px-2">
                                                    {owner.subscription?.plan ? (
                                                        <Badge variant={owner.subscription.is_lifetime ? 'success' : 'success'}>
                                                            {owner.subscription.plan.name}
                                                            {owner.subscription.is_lifetime && ' ∞'}
                                                        </Badge>
                                                    ) : (
                                                        <Badge variant="secondary">Sin plan</Badge>
                                                    )}
                                                </td>
                                                <td className="py-3 px-2">
                                                    {(() => {
                                                        const sub = owner.subscription;
                                                        if (!sub?.plan) return <Badge variant="secondary">Sin plan</Badge>;
                                                        const isActive = sub.is_lifetime || (sub.status === 'active' && (!sub.ends_at || new Date(sub.ends_at) >= new Date()));
                                                        return <Badge variant={isActive ? 'success' : 'destructive'}>{isActive ? 'Activo' : 'Vencido'}</Badge>;
                                                    })()}
                                                </td>
                                                <td className="py-3 px-2">{owner.owned_gyms?.length || 0}</td>
                                                <td className="py-3 px-2 text-right">
                                                    <div className="flex justify-end gap-1">
                                                        <Button variant="outline" size="icon" onClick={() => openEdit(owner)} title="Editar"><Pencil className="h-4 w-4" /></Button>
                                                        <Button variant="outline" size="icon" onClick={() => handleLifetimeFree(owner.id)} title="De por vida"><Crown className="h-4 w-4" /></Button>
                                                        <Button variant="outline" size="icon" onClick={() => handleDelete(owner.id)} title="Eliminar"><Trash2 className="h-4 w-4" /></Button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            {meta.last_page > 1 && (
                                <div className="flex justify-center gap-2 mt-4">
                                    <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Anterior</Button>
                                    <span className="flex items-center text-sm text-white/60">Página {meta.current_page} de {meta.last_page}</span>
                                    <Button variant="outline" size="sm" disabled={page >= meta.last_page} onClick={() => setPage(p => p + 1)}>Siguiente</Button>
                                </div>
                            )}
                        </>
                    )}
                </CardContent>
            </Card>

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="bg-[#0a0a0a] border-hairline-dark">
                    <DialogHeader>
                        <DialogTitle>{editing ? 'Editar Propietario' : 'Nuevo Propietario'}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSave} className="space-y-4">
                        {error && <div className="bg-[#0a0a0a] border border-hairline-dark text-white text-sm p-3 rounded-md">{error}</div>}
                        <div className="space-y-2">
                            <Label>Nombre</Label>
                            <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required />
                        </div>
                        <div className="space-y-2">
                            <Label>Email</Label>
                            <Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required />
                        </div>
                        <div className="space-y-2">
                            <Label>Contraseña {editing && '(dejar vacío para no cambiar)'}</Label>
                            <Input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} {...(!editing && { required: true, minLength: 8 })} />
                        </div>
                        <div className="space-y-2">
                            <Label>Teléfono</Label>
                            <Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
                        </div>
                        <div className="space-y-2">
                            <Label>Plan {editing && '(dejar igual para no cambiar)'}</Label>
                            <select className="flex h-9 w-full rounded-md border border-hairline-dark bg-[#0a0a0a] px-3 py-1 text-sm text-white" value={form.plan_id} onChange={e => setForm({ ...form, plan_id: e.target.value })}>
                                <option value="" className="bg-[#0a0a0a] text-white">Sin plan</option>
                                {plans.map(p => <option key={p.id} value={p.id} className="bg-[#0a0a0a] text-white">{p.name} ({p.max_gyms} gyms, {p.max_customers_per_gym} alumnos/gym)</option>)}
                            </select>
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
                            <Button type="submit" disabled={saving}>{saving ? 'Guardando...' : 'Guardar'}</Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}
